import {unquoteGitPath} from './artifact-paths'
import type {WriteOperation} from './artifact-types'
import type {Replacement} from './replacements'

export interface ParsedApplyPatchWrite {
  path: string
  previousPath?: string
  operation: WriteOperation
  content?: string
  replacements?: Replacement[]
}

export type HunkBufferResult = {kind: 'replacement'; value: Replacement} | {kind: 'unreconstructable'} | {kind: 'empty'}

export class HunkBuffer {
  readonly #find: string[] = []
  readonly #replace: string[] = []
  readonly oldStartLine?: number
  #oldRemaining?: number
  #newRemaining?: number
  #endOfFile?: true

  constructor(oldStartLine?: number, oldRemaining?: number, newRemaining?: number, endOfFile?: true) {
    this.oldStartLine = oldStartLine
    this.#oldRemaining = oldRemaining
    this.#newRemaining = newRemaining
    this.#endOfFile = endOfFile
  }

  get exhausted(): boolean {
    return this.#oldRemaining === 0 && this.#newRemaining === 0
  }

  pushContext(line: string): void {
    this.#find.push(line)
    this.#replace.push(line)
    if (this.#oldRemaining !== undefined) this.#oldRemaining--
    if (this.#newRemaining !== undefined) this.#newRemaining--
  }

  pushRemoval(line: string): void {
    this.#find.push(line)
    if (this.#oldRemaining !== undefined) this.#oldRemaining--
  }

  pushAddition(line: string): void {
    this.#replace.push(line)
    if (this.#newRemaining !== undefined) this.#newRemaining--
  }

  markEndOfFile(): void {
    this.#endOfFile = true
  }

  finish(): HunkBufferResult {
    if (this.#find.length === 0 && this.#replace.length === 0) return {kind: 'empty'}
    if (this.#find.length > 0 && this.#replace.length === 0) return {kind: 'unreconstructable'}
    const find = this.#find.join('\n')
    return {
      kind: 'replacement',
      value: {
        find,
        replace: this.#replace.join('\n'),
        lineBounded: true,
        // Joining gives an empty `find` for both no old lines and one empty old
        // line, so carry the count to tell them apart.
        ...(find.length === 0 ? {oldLineCount: this.#find.length} : {}),
        ...(this.#endOfFile ? {endOfFile: true} : {}),
        ...(this.oldStartLine === undefined ? {} : {oldStartLine: this.oldStartLine}),
      },
    }
  }
}

export function matchUnifiedHeader(
  line: string,
): {kind: 'old' | 'new'; path: string | undefined; devNull?: true} | undefined {
  const oldMatch = line.match(/^---\s(.*)$/)
  const kind = oldMatch ? 'old' : 'new'
  const match = oldMatch ?? line.match(/^\+\+\+\s(.*)$/)
  if (!match) return undefined
  const captured = match?.[1]?.split('\t')[0]?.trim()
  // `/dev/null` is distinct from a whitespace-only path: on the old side it marks
  // a file creation, on the new side a deletion.
  if (captured === '/dev/null') return {kind, path: undefined, devNull: true}
  if (!captured) return {kind, path: undefined}
  // Git wraps a path needing escapes in quotes, putting the `a/`/`b/` prefix
  // inside them, so decode before stripping it.
  const unquoted = /^"(?:[^"\\]|\\.)*"$/.test(captured) ? unquoteGitPath(captured.slice(1, -1)) : captured
  if (unquoted === '/dev/null') return {kind, path: undefined, devNull: true}
  const prefix = kind === 'old' ? 'a/' : 'b/'
  return {kind, path: unquoted.startsWith(prefix) ? unquoted.slice(2) : unquoted}
}

/**
 * Extract target paths, whole-file adds, and update hunks from an `apply_patch`
 * body. Both the `*** Add/Update/Delete File:` envelope and plain unified diff
 * headers reach this tool.
 *
 * `*** Add File:` lines carry the whole file prefixed with `+`, so they become
 * `content` and can open in the workbench. Update hunks instead become
 * replacements applied to a previously captured body. `*** Delete File:`, and a
 * pathful old header followed by `+++ /dev/null`, become a `deleted` write so the
 * reducer can drop the path.
 *
 * Paths are captured to end-of-line and trimmed; a repeating separator or lazy
 * capture next to `.*`/`\s*$` backtracks polynomially (CodeQL js/polynomial-redos).
 */
export function parseApplyPatchPaths(content: string): ParsedApplyPatchWrite[] {
  const results: ParsedApplyPatchWrite[] = []
  // Adjacent Add File blocks have no explicit terminator between them, so
  // track the open block across iterations and flush at the next marker.
  let openAdd: {path: string; lines: string[]} | undefined
  // A unified diff whose old header is `/dev/null` is a creation; its added
  // lines are captured as content, mirroring an `*** Add File:` block.
  let openCreate: {path: string; lines: string[]} | undefined
  let openUpdate:
    | {
        path: string
        previousPath?: string
        moveTo?: string
        replacements: Replacement[]
        reconstructable: boolean
        fromEnvelope: boolean
        hunk?: HunkBuffer
      }
    | undefined
  let unifiedPreviousPath: string | undefined
  let unifiedOldDevNull = false

  const flushOpenAdd = () => {
    if (!openAdd) return
    results.push({path: openAdd.path, operation: 'created', content: openAdd.lines.join('\n')})
    openAdd = undefined
  }

  const flushOpenCreate = () => {
    if (!openCreate) return
    results.push({path: openCreate.path, operation: 'created', content: openCreate.lines.join('\n')})
    openCreate = undefined
  }

  const flushHunk = () => {
    if (!openUpdate?.hunk) return
    const result = openUpdate.hunk.finish()
    if (result.kind === 'unreconstructable') openUpdate.reconstructable = false
    else if (result.kind === 'replacement') openUpdate.replacements.push(result.value)
    openUpdate.hunk = undefined
  }

  const flushOpenUpdate = () => {
    if (!openUpdate) return
    flushHunk()
    const path = openUpdate.moveTo ?? openUpdate.path
    const previousPath = openUpdate.moveTo === undefined ? openUpdate.previousPath : openUpdate.path
    results.push({
      path,
      ...(previousPath === undefined || path === previousPath ? {} : {previousPath}),
      operation: 'modified',
      ...(!openUpdate.reconstructable || openUpdate.replacements.length === 0
        ? {}
        : {replacements: openUpdate.replacements}),
    })
    openUpdate = undefined
  }

  const flushOpenBlock = () => {
    flushOpenAdd()
    flushOpenCreate()
    flushOpenUpdate()
  }

  for (const rawLine of content.split('\n')) {
    const line = rawLine.replace(/\r$/, '')
    const envelope = line.match(/^\*\*\*\s+(Add|Update|Delete)\s+File:(.*)$/)
    if (envelope) {
      unifiedPreviousPath = undefined
      unifiedOldDevNull = false
      flushOpenBlock()
      const path = envelope[2]?.trim()
      // A deletion produces no browsable artifact, but the reducer still needs
      // it to drop any artifact an earlier write opened for this path.
      if (envelope[1] === 'Delete') {
        if (path) results.push({path, operation: 'deleted'})
        continue
      }
      if (!path) continue
      if (envelope[1] === 'Add') {
        openAdd = {path, lines: []}
      } else {
        openUpdate = {path, replacements: [], reconstructable: true, fromEnvelope: true}
      }
      continue
    }

    if (line === '*** End of File' && openUpdate?.fromEnvelope) {
      openUpdate.hunk?.markEndOfFile()
      continue
    }

    if (openUpdate?.hunk?.exhausted) flushHunk()

    const move = line.match(/^\*\*\*\s+Move\s+to:(.*)$/)
    if (move && openUpdate?.fromEnvelope) {
      const path = move[1]?.trim()
      if (path) openUpdate.moveTo = path
      continue
    }

    if (/^\*\*\*\s+(Begin|End)\s+Patch\b/.test(line)) {
      unifiedPreviousPath = undefined
      unifiedOldDevNull = false
      flushOpenBlock()
      continue
    }

    if (openAdd) {
      openAdd.lines.push(line.startsWith('+') ? line.slice(1) : line)
      continue
    }

    if (line.startsWith('diff --git ')) {
      unifiedPreviousPath = undefined
      unifiedOldDevNull = false
      if (openUpdate?.fromEnvelope) continue
      flushOpenBlock()
      continue
    }

    // Inside a create body a `+++ …` line is added content, not a header: only a
    // `--- ` line can introduce the next file, and that clears the state below.
    if (openCreate && unifiedPreviousPath === undefined && !unifiedOldDevNull && line.startsWith('+')) {
      openCreate.lines.push(line.slice(1))
      continue
    }

    const unifiedHeader = openUpdate?.hunk === undefined ? matchUnifiedHeader(line) : undefined
    // A new file header ends any create block opened for the previous file.
    if (unifiedHeader) flushOpenCreate()
    if (unifiedHeader?.kind === 'old') {
      if (!openUpdate?.fromEnvelope) {
        unifiedPreviousPath = unifiedHeader.path
        unifiedOldDevNull = unifiedHeader.devNull === true
      }
      continue
    }
    if (unifiedHeader?.kind === 'new') {
      if (!openUpdate?.fromEnvelope) {
        flushOpenUpdate()
        if (unifiedHeader.path !== undefined) {
          if (unifiedOldDevNull) {
            // A `/dev/null` old header followed by a pathful new header is a
            // creation; capture the added lines as content.
            openCreate = {path: unifiedHeader.path, lines: []}
          } else {
            openUpdate = {
              path: unifiedHeader.path,
              ...(unifiedPreviousPath === undefined ? {} : {previousPath: unifiedPreviousPath}),
              replacements: [],
              reconstructable: true,
              fromEnvelope: false,
            }
          }
        } else if (unifiedPreviousPath !== undefined && unifiedHeader.devNull === true) {
          // Only an explicit `/dev/null` target is a deletion. A malformed
          // pathless header is not, and treating it as one would drop an
          // artifact and close its tab.
          results.push({path: unifiedPreviousPath, operation: 'deleted'})
        }
      }
      unifiedPreviousPath = undefined
      unifiedOldDevNull = false
      continue
    }

    if (openCreate) {
      if (line.startsWith('+')) openCreate.lines.push(line.slice(1))
      continue
    }

    if (!openUpdate) continue

    if (line.startsWith('\\ No newline at end of file')) {
      openUpdate.reconstructable = false
      continue
    }

    const hunkHeader = line.match(/^@@ -(\d+)(?:,(\d+))? \+\d+(?:,(\d+))? @@/)
    if (hunkHeader || /^@@(?:\s|$)/.test(line)) {
      flushHunk()
      openUpdate.hunk =
        hunkHeader === null
          ? new HunkBuffer()
          : new HunkBuffer(Number(hunkHeader[1]), Number(hunkHeader[2] ?? 1), Number(hunkHeader[3] ?? 1))
      continue
    }

    if (openUpdate.hunk === undefined && line.startsWith('index ')) continue

    if (line === '' && openUpdate.hunk) {
      openUpdate.hunk.pushContext('')
      continue
    }

    const marker = line[0]
    let hunkMarker: ' ' | '-' | '+'
    let hunkLine: string
    if (marker === ' ' || marker === '-' || marker === '+') {
      hunkMarker = marker
      hunkLine = line.slice(1)
    } else if (openUpdate.fromEnvelope) {
      hunkMarker = ' '
      hunkLine = line
    } else {
      continue
    }
    openUpdate.hunk ??= new HunkBuffer()
    if (hunkMarker === ' ') openUpdate.hunk.pushContext(hunkLine)
    else if (hunkMarker === '-') openUpdate.hunk.pushRemoval(hunkLine)
    else openUpdate.hunk.pushAddition(hunkLine)
  }

  flushOpenBlock()
  return results
}
